import axios from "axios";
import { request } from ".";
import { Id } from "src/utils/types";

type ApiEnvelope<T> = {
  success: boolean;
  data: T;
  message?: string;
};

export type NivelRisco =
  | "baixo"
  | "moderado"
  | "alto"
  | "critico"
  | "nao_informado";

export type ProntuarioStatus = "ativo" | "inativo" | "arquivado";

export type Prontuario = {
  id?: string;
  prontuario_id?: string;
  id_prontuario?: string;
  id_colaborador?: number;
  colaborador_id?: number;
  fk_collaborator_id?: number;
  colaborador?: {
    nome?: string;
    setor?: string;
    cargo?: string;
    unidade?: string;
  };
  colaborador_nome?: string;
  nome?: string;
  empresa?: {
    id?: number;
    nome?: string;
    trade_name?: string;
    social_reason?: string;
  };
  id_empresa?: number;
  empresa_nome?: string;
  queixa_inicial?: string;
  resumo_geral?: string;
  contexto_clinico?: string;
  objetivo_terapeutico?: string;
  nivel_risco?: NivelRisco;
  observacoes_gerais?: string;
  restricoes_importantes?: string;
  contato_emergencia_nome?: string;
  contato_emergencia_telefone?: string;
  contato_emergencia_parentesco?: string;
  medicamentos_em_uso_resumo?: string;
  alergias?: string;
  condicoes_relevantes?: string;
  status?: ProntuarioStatus;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type ProntuarioUpdatePayload = Partial<{
  queixa_inicial: string;
  resumo_geral: string;
  contexto_clinico: string;
  objetivo_terapeutico: string;
  nivel_risco: NivelRisco;
  observacoes_gerais: string;
  restricoes_importantes: string;
  contato_emergencia_nome: string;
  contato_emergencia_telefone: string;
  contato_emergencia_parentesco: string;
  medicamentos_em_uso_resumo: string;
  alergias: string;
  condicoes_relevantes: string;
  status: ProntuarioStatus;
}>;

export type Medication = {
  id?: string;
  medication_id?: string;
  nome?: string;
  name?: string;
  principio_ativo?: string;
  active_ingredient?: string;
  concentracao?: string;
  concentration?: string;
  forma_farmaceutica?: string;
  pharmaceutical_form?: string;
  via?: string;
  route?: string;
  bula_paciente_url?: string;
  patient_leaflet_url?: string;
  bula_profissional_url?: string;
  professional_leaflet_url?: string;
  bula_image_urls?: string[] | string | null;
  [key: string]: unknown;
};

export type ProntuarioMedicationStatus =
  | "em_uso"
  | "suspenso"
  | "finalizado"
  | "uso_eventual"
  | "desconhecido";

export type AddMedicationPayload = {
  medication_id: string;
  dosage: string;
  frequency: string;
  usage_route: string;
  start_date: string;
  end_date: string | null;
  status: ProntuarioMedicationStatus;
  notes: string;
  prescribed_by: string;
};

export type ProntuarioMedication = {
  id?: string;
  link_id?: string;
  prontuario_medication_id?: string;
  medication_id?: string;
  medication?: Medication;
  medicamento?: Medication;
  nome?: string;
  name?: string;
  principio_ativo?: string;
  active_ingredient?: string;
  concentracao?: string;
  concentration?: string;
  forma_farmaceutica?: string;
  pharmaceutical_form?: string;
  via?: string;
  route?: string;
  dosage?: string;
  dose?: string;
  frequency?: string;
  frequencia?: string;
  usage_route?: string;
  start_date?: string;
  end_date?: string | null;
  status?: ProntuarioMedicationStatus | string;
  notes?: string;
  observacoes?: string;
  prescribed_by?: string;
  bula_paciente_url?: string;
  patient_leaflet_url?: string;
  bula_profissional_url?: string;
  professional_leaflet_url?: string;
  bula_image_urls?: string[] | string | null;
  [key: string]: unknown;
};

export type SessionSourceType = "service" | "schedule";

export type ConsultaStatus =
  | "marcada"
  | "realizada"
  | "paciente_faltou"
  | "cancelada";

/**
 * Item do dropdown de sessões: pode ser um atendimento já realizado (`services`)
 * ou apenas um agendamento futuro (`session_schedules`). Atendimentos avulsos não
 * possuem agendamento, por isso `schedule_id` pode ser nulo — a identificação
 * única do item é o `selection_id` ("service:<id>" ou "schedule:<id>").
 */
export type SessionSchedule = {
  selection_id?: string;
  value?: string;
  source_type?: SessionSourceType;
  id?: number | null;
  service_id?: number | null;
  fk_service_id?: number | null;
  schedule_id?: number | null;
  fk_session_schedule_id?: number | null;
  title?: string;
  patient_name?: string;
  status_consulta?: ConsultaStatus | string | null;
  data_realizacao?: string | null;
  date_of_service?: string | null;
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  [key: string]: unknown;
};

export type ClinicalNoteType =
  | "avaliacao_inicial"
  | "evolucao"
  | "retorno"
  | "crise"
  | "encaminhamento"
  | "encerramento"
  | "observacao"
  | "soap";

export type ClinicalNote = {
  id?: string;
  note_id?: string;
  anotacao_id?: string;
  fk_service_id?: number | null;
  fk_session_schedule_id?: number | null;
  data_atendimento?: string;
  tipo_anotacao?: ClinicalNoteType | string;
  titulo?: string;
  queixa_relato?: string;
  observacao_clinica?: string;
  intervencoes_realizadas?: string;
  resposta_do_paciente?: string;
  evolucao_percebida?: string;
  hipoteses_clinicas?: string;
  fatores_de_risco?: string;
  fatores_de_protecao?: string;
  plano_conduta?: string;
  encaminhamentos?: string;
  soap_subjetivo?: string;
  soap_objetivo?: string;
  soap_avaliacao?: string;
  soap_plano?: string;
  humor_nota?: number;
  ansiedade_nota?: number;
  adesao_nota?: number;
  risco_nota?: number;
  evolucao_nota?: number;
  tags?: string[];
  status?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export type ClinicalNotePayload = Partial<{
  fk_service_id: number | null;
  fk_session_schedule_id: number | null;
  data_atendimento: string;
  tipo_anotacao: ClinicalNoteType;
  titulo: string;
  queixa_relato: string;
  observacao_clinica: string;
  intervencoes_realizadas: string;
  resposta_do_paciente: string;
  evolucao_percebida: string;
  hipoteses_clinicas: string;
  fatores_de_risco: string;
  fatores_de_protecao: string;
  plano_conduta: string;
  encaminhamentos: string;
  soap_subjetivo: string;
  soap_objetivo: string;
  soap_avaliacao: string;
  soap_plano: string;
  humor_nota: number;
  ansiedade_nota: number;
  adesao_nota: number;
  risco_nota: number;
  evolucao_nota: number;
  tags: string[];
}>;

export type HistoricoAtendimento = {
  id?: number;
  id_atendimento?: number;
  date_of_service?: string;
  atendimento?: string;
  psicologo?: { id?: number; name?: string; type?: string } | string;
  psychologist_name?: string;
  empresa?: { id?: number; trade_name?: string; nome?: string } | string;
  trade_name?: string;
  area?: string;
  cidade?: string;
  city?: string;
  queixa_inicial?: string;
  complaint?: string;
  observacoes?: string;
  annotations?: string;
  pago?: boolean;
  paid?: boolean;
  payment_status?: string;
  service_status?: { status?: string; service_id?: number; updated_at?: string } | string | null;
  status_atendimento?: string;
  [key: string]: unknown;
};

export type ProntuarioApiData = {
  possui_prontuario: boolean;
  prontuario: Prontuario | null;
  colaborador: {
    id_colaborador?: number;
    nome?: string;
    setor?: string;
    cargo?: string;
    id_empresa?: number;
    unidade?: string;
  } | null;
  empresa: {
    id?: number;
    nome?: string;
    trade_name?: string;
    social_reason?: string;
  } | null;
  anotacoes?: ClinicalNote[];
  medicamentos?: ProntuarioMedication[];
  historico_atendimentos?: HistoricoAtendimento[];
};

export type PacienteFilters = Partial<{
  q: string;
  empresa: string;
  setor: string;
  cargo: string;
  status_prontuario: string;
  nivel_risco: string;
}>;

export type Paciente = {
  id_colaborador: number;
  nome: string;
  setor?: string;
  cargo?: string;
  id_empresa?: number;
  empresa_nome?: string;
  ultimo_atendimento_id?: number;
  ultimo_atendimento_data?: string;
  ultimo_psicologo_id?: number;
  possui_prontuario?: boolean;
  prontuario_id?: string;
  prontuario_status?: string;
  nivel_risco?: string;
  [key: string]: unknown;
};

const pacienteFilterKeys: Array<keyof PacienteFilters> = [
  "q",
  "empresa",
  "setor",
  "cargo",
  "status_prontuario",
  "nivel_risco",
];

const unwrapData = <T>(payload: T | ApiEnvelope<T>): T => {
  if (
    payload &&
    typeof payload === "object" &&
    "success" in payload &&
    "data" in payload
  ) {
    const responsePayload = payload as ApiEnvelope<T>;

    if (!responsePayload.success) {
      throw new Error(responsePayload.message ?? "Não foi possível processar a solicitação.");
    }

    return responsePayload.data;
  }

  return payload as T;
};

export const getProntuarioId = (prontuario?: Prontuario | null): string => {
  return String(
    prontuario?.id ??
      prontuario?.prontuario_id ??
      prontuario?.id_prontuario ??
      ""
  );
};

export const getErrorStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }

  return undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  fallback = "Não foi possível processar a solicitação."
): string => {
  const status = getErrorStatus(error);

  if (status === 401) {
    return "Sessão expirada ou usuário não autenticado.";
  }

  if (status === 403) {
    return "Acesso não autorizado ao prontuário.";
  }

  if (status === 404) {
    return "Registro não encontrado.";
  }

  if (status === 500) {
    return "Erro inesperado. Tente novamente em instantes.";
  }

  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; error?: string } | undefined;
    return data?.message ?? data?.error ?? error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

export async function getProntuarioByColaborador(idColaborador: Id): Promise<ProntuarioApiData> {
  const response = await request.get<ApiEnvelope<ProntuarioApiData> | ProntuarioApiData>(
    `/api/prontuarios/colaborador/${idColaborador}`
  );

  return unwrapData<ProntuarioApiData>(response.data);
}

export async function createProntuario(
  idColaborador: Id,
  payload: ProntuarioUpdatePayload = {}
): Promise<ProntuarioApiData> {
  const response = await request.post<ApiEnvelope<ProntuarioApiData> | ProntuarioApiData>(
    `/api/prontuarios/colaborador/${idColaborador}`,
    payload
  );

  return unwrapData<ProntuarioApiData>(response.data);
}

export async function updateProntuario(
  prontuarioId: Id,
  payload: ProntuarioUpdatePayload
): Promise<Prontuario> {
  const response = await request.patch<ApiEnvelope<Prontuario> | Prontuario>(
    `/api/prontuarios/${prontuarioId}`,
    payload
  );

  return unwrapData<Prontuario>(response.data);
}

export async function getClinicalNotes(prontuarioId: Id): Promise<ClinicalNote[]> {
  const response = await request.get<ApiEnvelope<ClinicalNote[]> | ClinicalNote[]>(
    `/api/prontuarios/${prontuarioId}/anotacoes`
  );

  return unwrapData<ClinicalNote[]>(response.data) ?? [];
}

export async function createClinicalNote(
  prontuarioId: Id,
  payload: ClinicalNotePayload
): Promise<ClinicalNote> {
  const response = await request.post<ApiEnvelope<ClinicalNote> | ClinicalNote>(
    `/api/prontuarios/${prontuarioId}/anotacoes`,
    payload
  );

  return unwrapData<ClinicalNote>(response.data);
}

export async function updateClinicalNote(
  noteId: Id,
  payload: ClinicalNotePayload
): Promise<ClinicalNote> {
  const response = await request.patch<ApiEnvelope<ClinicalNote> | ClinicalNote>(
    `/api/prontuarios/anotacoes/${noteId}`,
    payload
  );

  return unwrapData<ClinicalNote>(response.data);
}

export async function deleteClinicalNote(noteId: Id): Promise<void> {
  await request.delete(`/api/prontuarios/anotacoes/${noteId}`);
}

export async function searchMedications(query: string): Promise<Medication[]> {
  const response = await request.get<ApiEnvelope<Medication[]> | Medication[]>(
    "/api/medicamentos/search",
    {
      params: {
        q: query,
      },
    }
  );

  return unwrapData<Medication[]>(response.data) ?? [];
}

export async function getProntuarioMedications(
  prontuarioId: Id
): Promise<ProntuarioMedication[]> {
  const response = await request.get<
    ApiEnvelope<ProntuarioMedication[]> | ProntuarioMedication[]
  >(`/api/prontuarios/${prontuarioId}/medicamentos`);

  return unwrapData<ProntuarioMedication[]>(response.data) ?? [];
}

export async function addMedicationToProntuario(
  prontuarioId: Id,
  payload: AddMedicationPayload
): Promise<ProntuarioMedication> {
  const response = await request.post<
    ApiEnvelope<ProntuarioMedication> | ProntuarioMedication
  >(`/api/prontuarios/${prontuarioId}/medicamentos`, payload);

  return unwrapData<ProntuarioMedication>(response.data);
}

export async function finishProntuarioMedication(
  prontuarioId: Id,
  linkId: Id
): Promise<void> {
  await request.delete(`/api/prontuarios/${prontuarioId}/medicamentos/${linkId}`);
}

export async function getCollaboratorSessions(
  idColaborador: Id
): Promise<SessionSchedule[]> {
  const response = await request.get<ApiEnvelope<SessionSchedule[]> | SessionSchedule[]>(
    `/api/prontuarios/colaborador/${idColaborador}/sessoes`
  );

  return unwrapData<SessionSchedule[]>(response.data) ?? [];
}

export async function getHistoricoAtendimentos(
  idColaborador: Id
): Promise<HistoricoAtendimento[]> {
  const response = await request.get<
    ApiEnvelope<HistoricoAtendimento[]> | HistoricoAtendimento[]
  >(`/api/prontuarios/colaborador/${idColaborador}/historico-atendimentos`);

  return unwrapData<HistoricoAtendimento[]>(response.data) ?? [];
}

export async function getPacientes(filters: PacienteFilters = {}): Promise<Paciente[]> {
  const params = new URLSearchParams();

  pacienteFilterKeys.forEach((key) => {
    const value = filters[key];

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params.append(key, String(value));
    }
  });

  const query = params.toString();
  const url = query ? `/api/pacientes?${query}` : "/api/pacientes";
  const response = await request.get<ApiEnvelope<Paciente[]> | Paciente[]>(url);

  return unwrapData<Paciente[]>(response.data) ?? [];
}
